import type { QueryClient } from "@tanstack/react-query";

import { qk } from "./queries";

/**
 * Shared optimistic helpers for marking notifications read. The bell and the
 * full Notifications page both adjust the unread badge instantly and reconcile
 * on settle; this centralises that logic so the two stay consistent.
 */

/** Refresh the feed/preview AND the bell badge (independent query keys). */
export function invalidateNotifications(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: qk.notifications });
  void queryClient.invalidateQueries({ queryKey: qk.unread });
}

/**
 * Optimistically adjust the cached unread count. Returns the previous value so
 * the caller can roll back on error.
 */
export function patchUnread(
  queryClient: QueryClient,
  next: (n: number) => number,
): number | undefined {
  const prev = queryClient.getQueryData<number>(qk.unread);
  queryClient.setQueryData<number>(qk.unread, (n) =>
    typeof n === "number" ? Math.max(0, next(n)) : n,
  );
  return prev;
}

/** Restore a snapshot taken by `patchUnread`. */
export function restoreUnread(
  queryClient: QueryClient,
  prev: number | undefined,
): void {
  queryClient.setQueryData(qk.unread, prev);
}
